import { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { ChevronDown, ChevronRight } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import { NavItem } from './NavItem'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'

interface NavGroupItem {
  label: string
  href: string
  icon?: LucideIcon
}

interface NavGroupProps {
  id: string
  label: string
  icon: LucideIcon
  items: NavGroupItem[]
  defaultExpanded?: boolean
  /** Icon-rail mode: render a single icon that opens a flyout of the items. */
  collapsed?: boolean
  /** Called after a child link is clicked (e.g. close a mobile drawer). */
  onNavigate?: () => void
}

export function NavGroup({
  id,
  label,
  icon: Icon,
  items,
  defaultExpanded = false,
  collapsed = false,
  onNavigate,
}: NavGroupProps) {
  const location = useLocation()
  const containsActive = items.some(
    (item) =>
      location.pathname === item.href ||
      (item.href !== '/' && location.pathname.startsWith(item.href))
  )

  const storageKey = `nav-group-${id}-expanded`
  const [userOpen, setUserOpen] = useState(() => {
    const stored = localStorage.getItem(storageKey)
    return stored !== null ? stored === 'true' : defaultExpanded
  })

  useEffect(() => {
    localStorage.setItem(storageKey, String(userOpen))
  }, [userOpen, storageKey])

  // The group you're currently in is always revealed; otherwise honor the
  // user's stored preference / default.
  const isExpanded = userOpen || containsActive

  const [flyoutOpen, setFlyoutOpen] = useState(false)

  // Collapsed icon-rail: a single icon button opens a click/keyboard flyout.
  if (collapsed) {
    return (
      <Popover open={flyoutOpen} onOpenChange={setFlyoutOpen}>
        <PopoverTrigger asChild>
          <button
            className={cn(
              'flex w-full items-center justify-center rounded-md py-1 transition-colors',
              'hover:bg-accent hover:text-accent-foreground',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              containsActive && 'bg-primary text-primary-foreground'
            )}
            aria-label={`${label} navigation`}
            aria-haspopup="menu"
          >
            <Icon className="h-5 w-5" aria-hidden="true" />
          </button>
        </PopoverTrigger>
        <PopoverContent side="right" align="start" className="w-52 p-1">
          <p className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">{label}</p>
          <div className="space-y-0.5">
            {items.map((item) => (
              <NavItem
                key={item.href}
                label={item.label}
                href={item.href}
                icon={item.icon}
                onNavigate={() => {
                  setFlyoutOpen(false)
                  onNavigate?.()
                }}
              />
            ))}
          </div>
        </PopoverContent>
      </Popover>
    )
  }

  return (
    <div>
      <button
        onClick={() => setUserOpen((v) => !v)}
        className={cn(
          'w-full flex items-center gap-2 px-3 py-1 text-sm font-medium rounded-md transition-colors',
          'hover:bg-accent hover:text-accent-foreground',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
        )}
        aria-expanded={isExpanded}
        aria-controls={`nav-group-${id}`}
        aria-label={`${label} navigation group`}
      >
        <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
        <span className="flex-1 text-left">{label}</span>
        {isExpanded ? (
          <ChevronDown className="h-4 w-4 shrink-0" aria-hidden="true" />
        ) : (
          <ChevronRight className="h-4 w-4 shrink-0" aria-hidden="true" />
        )}
      </button>
      <div
        id={`nav-group-${id}`}
        className={cn(
          'overflow-hidden transition-all duration-200',
          isExpanded ? 'max-h-screen' : 'max-h-0'
        )}
        role="region"
      >
        <div className="py-0.5 space-y-0.5">
          {items.map((item) => (
            <NavItem
              key={item.href}
              label={item.label}
              href={item.href}
              nested
              showPin
              onNavigate={onNavigate}
            />
          ))}
        </div>
      </div>
    </div>
  )
}
